import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import { 
  Users, Clock, CheckCircle2, XCircle, AlertTriangle, 
  RefreshCw, ArrowLeft, MapPin, Calendar, Eye, Download,
  UserCheck, UserX, Timer, AlertCircle, Ban, LogIn, LogOut, Bell
} from 'lucide-react';
import { formatTime12h, formatTimeRange, formatDateTime12h, formatDateMMDDYYYY } from '../../utils/timeFormat';
import { resolveProfilePhoto } from '../../utils/profilePhoto';

const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [overview, setOverview] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState('today');
  const [showNoShowDialog, setShowNoShowDialog] = useState(false);
  const [noShowBookingId, setNoShowBookingId] = useState(null);
  const [noShowReason, setNoShowReason] = useState('');
  const [markingNoShow, setMarkingNoShow] = useState(false);
  
  // Real-time notification tracking
  const lastNotificationIdRef = useRef(null);
  const [liveNotifications, setLiveNotifications] = useState([]);

  // Poll for clock notifications (real-time toasts)
  const pollClockNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications/client/unread', {
        params: { types: 'clock_in,clock_out', limit: 5 }
      });
      
      const notifications = res.data || [];
      
      // Show toast for new notifications
      notifications.forEach(notif => {
        // Only show if we haven't shown this one before
        if (!lastNotificationIdRef.current || notif.id > lastNotificationIdRef.current) {
          const isClockIn = notif.type === 'clock_in';
          
          // Show custom toast with icon
          toast.custom((t) => (
            <div className="flex items-center gap-3 bg-white border border-gray-200 shadow-lg rounded-lg p-4 min-w-[300px]">
              <div className={`p-2 rounded-full ${isClockIn ? 'bg-green-100' : 'bg-blue-100'}`}>
                {isClockIn ? (
                  <LogIn className="w-5 h-5 text-green-600" />
                ) : (
                  <LogOut className="w-5 h-5 text-blue-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 text-sm">{notif.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{notif.message}</p>
              </div>
              <Bell className="w-4 h-4 text-amber-500 animate-pulse" />
            </div>
          ), {
            duration: 5000,
            position: 'top-right'
          });
          
          // Track this notification
          setLiveNotifications(prev => [notif, ...prev.slice(0, 9)]);
        }
      });
      
      // Update last notification ID
      if (notifications.length > 0) {
        lastNotificationIdRef.current = Math.max(...notifications.map(n => n.id));
        
        // Mark as read
        notifications.forEach(async (notif) => {
          try {
            await api.patch(`/notifications/${notif.id}/read`);
          } catch (e) {
            // Ignore read errors
          }
        });
      }
    } catch (error) {
      // Silent fail for notification polling
    }
  }, []);

  // Fetch client's shifts
  const fetchShifts = useCallback(async () => {
    try {
      const response = await api.get('/client/shifts', {
        params: { status: 'active', limit: 50 }
      });
      setShifts(response.data || []);
      if (response.data?.length > 0 && !selectedShift) {
        setSelectedShift(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedShift]);

  // Fetch attendance overview for selected shift
  const fetchOverview = useCallback(async () => {
    if (!selectedShift) return;
    
    try {
      const response = await api.get(`/attendance/overview/${selectedShift}`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch overview:', error);
      setOverview(null);
    } finally {
      setRefreshing(false);
    }
  }, [selectedShift]);

  useEffect(() => {
    fetchShifts();
    fetchDashboardSummary();
    
    // Start polling for clock notifications (every 5 seconds for real-time feel)
    pollClockNotifications();
    const notifInterval = setInterval(pollClockNotifications, 5000);
    
    return () => clearInterval(notifInterval);
  }, [fetchShifts, pollClockNotifications]);

  // Fetch dashboard summary with late arrivals and no-shows
  const fetchDashboardSummary = async () => {
    try {
      const response = await api.get('/attendance/dashboard/summary');
      setDashboardSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard summary:', error);
    }
  };

  useEffect(() => {
    if (selectedShift) {
      fetchOverview();
      // Auto-refresh every 15 seconds
      const interval = setInterval(fetchOverview, 15000);
      return () => clearInterval(interval);
    }
  }, [selectedShift, fetchOverview]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOverview();
    fetchDashboardSummary();
  };

  // Mark talent as no-show
  const handleMarkNoShow = async () => {
    if (!noShowBookingId) return;
    setMarkingNoShow(true);
    try {
      await api.post(`/attendance/mark-no-show/${noShowBookingId}`, null, {
        params: { reason: noShowReason || 'No reason provided' }
      });
      toast.success('Talent marked as no-show');
      setShowNoShowDialog(false);
      setNoShowBookingId(null);
      setNoShowReason('');
      fetchOverview();
      fetchDashboardSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark no-show');
    } finally {
      setMarkingNoShow(false);
    }
  };

  const downloadPDF = async () => {
    if (!selectedShift) return;
    try {
      const response = await api.get(`/attendance/pdf/${selectedShift}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `signin_sheet.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sign-in sheet downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getAttendancePercentage = () => {
    if (!overview) return 0;
    const { attendance_summary } = overview;
    if (attendance_summary.total === 0) return 0;
    return Math.round(((attendance_summary.checked_in + attendance_summary.checked_out) / attendance_summary.total) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Attendance Dashboard</h1>
                <p className="text-sm text-slate-500">Real-time event attendance monitoring</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadPDF}
                disabled={!selectedShift}
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Event Selector */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="flex-grow">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Select Event</label>
                <Select value={selectedShift || ''} onValueChange={setSelectedShift}>
                  <SelectTrigger className="w-full md:w-96" data-testid="shift-selector">
                    <SelectValue placeholder="Select an event..." />
                  </SelectTrigger>
                  <SelectContent>
                    {shifts.map((shift) => (
                      <SelectItem key={shift.id} value={shift.id}>
                        {shift.venue_name} - {formatDateMMDDYYYY(shift.shift_date)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedShift && (
                <Link to={`/client/job-overview/${selectedShift}`}>
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4 mr-2" />
                    Full Overview
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {shifts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No Active Events</h3>
              <p className="text-slate-500 mt-1">You don't have any active events to monitor.</p>
              <Link to="/client/shifts/create">
                <Button className="mt-4">Create New Event</Button>
              </Link>
            </CardContent>
          </Card>
        ) : overview ? (
          <>
            {/* Overall Summary from Dashboard API — Iter-162a mobile-safe layout */}
            {dashboardSummary && (
              <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold">Overall Attendance Summary</h3>
                      <p className="text-sm text-slate-300">Across all your events</p>
                    </div>
                    <div className="grid grid-cols-4 gap-1.5 sm:gap-6 text-center w-full sm:w-auto">
                      <div>
                        <p className="text-xl sm:text-2xl font-bold">{dashboardSummary.summary.total_checked_in}</p>
                        <p className="text-[10px] sm:text-xs text-slate-300 leading-tight">Checked In</p>
                      </div>
                      <div>
                        <p className="text-xl sm:text-2xl font-bold text-amber-400">{dashboardSummary.summary.total_late_arrivals}</p>
                        <p className="text-[10px] sm:text-xs text-slate-300 leading-tight">Late Arrivals</p>
                      </div>
                      <div>
                        <p className="text-xl sm:text-2xl font-bold text-red-400">{dashboardSummary.summary.total_no_shows}</p>
                        <p className="text-[10px] sm:text-xs text-slate-300 leading-tight">No-Shows</p>
                      </div>
                      <div>
                        <p className="text-xl sm:text-2xl font-bold text-cyan-400">{dashboardSummary.summary.overall_attendance_rate}%</p>
                        <p className="text-[10px] sm:text-xs text-slate-300 leading-tight">Attendance Rate</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Card className="bg-gradient-to-br from-slate-50 to-slate-100">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <Users className="w-8 h-8 text-slate-400" />
                    <span className="text-3xl font-bold text-slate-700">
                      {overview.attendance_summary.total}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">Total Staff</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <UserCheck className="w-8 h-8 text-green-500" />
                    <span className="text-3xl font-bold text-green-600">
                      {overview.attendance_summary.checked_in}
                    </span>
                  </div>
                  <p className="text-sm text-green-700 mt-2">Checked In</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <CheckCircle2 className="w-8 h-8 text-blue-500" />
                    <span className="text-3xl font-bold text-blue-600">
                      {overview.attendance_summary.checked_out}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700 mt-2">Checked Out</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <AlertCircle className="w-8 h-8 text-amber-500" />
                    <span className="text-3xl font-bold text-amber-600">
                      {overview.attendance_summary.late_arrivals || 0}
                    </span>
                  </div>
                  <p className="text-sm text-amber-700 mt-2">Late Arrivals</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-red-100">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <Ban className="w-8 h-8 text-red-500" />
                    <span className="text-3xl font-bold text-red-600">
                      {overview.attendance_summary.no_shows || 0}
                    </span>
                  </div>
                  <p className="text-sm text-red-700 mt-2">No-Shows</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 rounded-full border-4 border-cyan-500 flex items-center justify-center">
                      <span className="text-lg font-bold text-cyan-600">{getAttendancePercentage()}%</span>
                    </div>
                  </div>
                  <p className="text-sm text-cyan-700 mt-2">Attendance Rate</p>
                </CardContent>
              </Card>
            </div>

            {/* Event Details */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>{overview.shift.venue_name}</CardTitle>
                    <CardDescription className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDateMMDDYYYY(overview.shift.shift_date)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTimeRange(overview.shift.start_time, overview.shift.end_time)}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {overview.shift.city}, {overview.shift.state}
                      </span>
                    </CardDescription>
                  </div>
                  {overview.qr_code_token && (
                    <Badge variant="outline" className="text-cyan-600">
                      QR: {overview.qr_code_token}
                    </Badge>
                  )}
                  {/* Iter-291 — Manage QR + attendance methods */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/shifts/${selectedShift}/qr`)}
                    data-testid="open-qr-manager-btn"
                    className="ml-2"
                  >
                    Manage QR
                  </Button>
                </div>
              </CardHeader>
            </Card>

            {/* Staff List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Staff Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {overview.staff.map((member) => (
                    <div 
                      key={member.booking_id}
                      className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                        member.attendance_status === 'checked_in' ? 'bg-green-50 border-green-200' :
                        member.attendance_status === 'checked_out' ? 'bg-blue-50 border-blue-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                      data-testid={`attendance-row-${member.user_id}`}
                    >
                      {/* Status Indicator */}
                      <div className={`w-3 h-3 rounded-full ${
                        member.attendance_status === 'checked_in' ? 'bg-green-500 animate-pulse' :
                        member.attendance_status === 'checked_out' ? 'bg-blue-500' :
                        'bg-slate-300'
                      }`} />

                      {/* Photo */}
                      <div className="flex-shrink-0">
                        {(() => {
                          const ph = resolveProfilePhoto(member);
                          return ph ? (
                            <img
                              src={ph}
                              alt={member.first_name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                              <span className="text-sm font-medium text-slate-500">
                                {member.first_name?.[0]}{member.last_name?.[0]}
                              </span>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Info */}
                      <div className="flex-grow min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {member.first_name} {member.last_name}
                        </p>
                        <p className="text-xs text-slate-500">{member.role_name}</p>
                      </div>

                      {/* Status Badge */}
                      <Badge variant={
                        member.attendance_status === 'checked_in' ? 'default' :
                        member.attendance_status === 'checked_out' ? 'secondary' :
                        'outline'
                      } className={
                        member.attendance_status === 'checked_in' ? 'bg-green-500' :
                        member.attendance_status === 'checked_out' ? 'bg-blue-500' :
                        ''
                      }>
                        {member.attendance_status === 'checked_in' ? 'Checked In' :
                         member.attendance_status === 'checked_out' ? 'Checked Out' :
                         'Pending'}
                      </Badge>

                      {/* Times */}
                      <div className="text-right text-xs text-slate-500 min-w-[80px]">
                        {member.checkin_time && (
                          <div className="text-green-600">
                            In: {formatDateTime12h(member.checkin_time)}
                          </div>
                        )}
                        {member.checkout_time && (
                          <div className="text-blue-600">
                            Out: {formatDateTime12h(member.checkout_time)}
                          </div>
                        )}
                      </div>

                      {/* Verification */}
                      {member.checkin_verification && member.checkin_verification !== 'unverified' && (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" title="GPS Verified" />
                      )}

                      {/* Mark No-Show Button - only show for pending staff */}
                      {member.attendance_status !== 'checked_in' && member.attendance_status !== 'checked_out' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNoShowBookingId(member.booking_id);
                            setShowNoShowDialog(true);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`no-show-btn-${member.booking_id}`}
                        >
                          <Ban className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  {overview.staff.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      No staff assigned to this event yet.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Select an Event</h3>
              <p className="text-slate-500 mt-1">Choose an event from the dropdown to view attendance.</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* No-Show Dialog */}
      <Dialog open={showNoShowDialog} onOpenChange={setShowNoShowDialog}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Mark as No-Show
            </DialogTitle>
            <DialogDescription>
              This will mark the talent as a no-show for this event. This action is logged.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
            <textarea
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              placeholder="e.g., Did not arrive, No communication, etc."
              value={noShowReason}
              onChange={(e) => setNoShowReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleMarkNoShow}
              disabled={markingNoShow}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {markingNoShow ? 'Marking...' : 'Mark No-Show'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendanceDashboard;
